import { defineStore } from 'pinia'
import type { Dish, SelectedDish, Order, selectedAddOn } from '~/types'
import { useSetSafeStorage } from '~/composables/useSetSafeStorage'
import { cloneDeep } from 'lodash-es'

export const useDishesStore = defineStore('dishes', () => {
  const { $axios, $i18n } = useNuxtApp()
  const alertyStore = useAlertyStore()
  const { showAlert } = alertyStore
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)
  const dishes = ref<any[]>([])
  const cat_ids = ref<number[]>([])
  const singleDish = ref<Dish | {}>({})
  const fetched_ids = ref<number[]>([])
  const dishesLanguage = ref<string>('')
  const applied_taxes = ref<any[]>([])
  const applied_fees = ref<any[]>([])
  const product_types = ref<any[]>([])
  const symbol = ref<string>('$')
  const iso_code = ref<string>('USD')
  const selectedDishes = ref<SelectedDish[]>([])
  const showLoginRequired = ref<boolean>(false)
  const storedCanceledOrdersData = useSafeStorage('canceledOrders')
  const isLoadingDishes = ref<boolean>(true)
  const discountCode = ref<string>('')
  const tips = ref<number | null>(Number(useSafeStorage('tips')) || null)
  const instractions = ref<string>('')
  const searchQuery = ref<string>('')
  const orders = ref<Order[] | []>([])
  const lastOrder = ref<Order | null>(null)
  const transactionVersion = ref<number>(0)
  const dishDetailsCache = ref<Record<string, Dish>>({})
  const setRestaurant = useRestaurantStore()
  const categoriesStore = useCategoriesStore()
  const isAnyOpenedDishDesc = ref(false)
  const ordersIds = ref<{ [key: string]: { [key: number]: string }[] }>({})
  const deliveryOrdersIds = ref<{ [key: string]: { [key: number]: string }[] }>({})
  const showingSingleOrder = ref<Order | null>(null)
  const { categories } = storeToRefs(categoriesStore)
  const { slug, tableId, menuIds, activeMenuId, experience, service_method, selected_service_method } = storeToRefs(setRestaurant)

  const getActiveServiceMethod = () => (
    selected_service_method.value ||
    (process.client ? useSafeStorage('_selected_service_method') : null) ||
    (service_method.value.length === 1 ? service_method.value[0] : 'dine_in')
  )

  const getCanceledOrderSnapshot = (orderID: number) => {
    try {
      const canceledOrders = JSON.parse(useSafeStorage('canceledOrders') || storedCanceledOrdersData)
      const slugKey = slug.value || 'default'
      return canceledOrders?.[slugKey]?.[String(orderID)] ?? null
    } catch (e) {
      return null
    }
  }

  const totalPrice = computed(() => {
    if (!selectedDishes.value.length) return 0

    return selectedDishes.value.reduce((total, dish) => {
      let price = parseFloat(
        String(
          dish.selectedCombination?.price ||
          (typeof dish.computed_price === 'string' ? Number(dish.computed_price) : dish.price) || 0
        )
      )
      if (dish.selectedAddOns.length) {
        let addOnsPrice: number = dish.selectedAddOns.reduce(
          (addOnTotal: number, addOn: selectedAddOn) => {
            if (addOn.price_adjustment === 'subtract')
              return addOnTotal - addOn.count * Number(addOn.amount)
            return addOnTotal + addOn.count * Number(addOn.amount)
          },
          0
        )
        return total + price * dish.count + addOnsPrice * dish.count
      }

      return total + price * dish.count
    }, 0)
  })

  const totalRulesPrice = computed(() => {
    const taxesTotal = applied_taxes.value.reduce((total, tax) => {
      if (tax.included_in_price) return total;
      return total + Number(tax.calculated_amount)
    }, 0)
    const feesTotal = applied_fees.value.reduce((total, fee) => {
      return total + Number(fee.calculated_amount)
    }, 0)
    return taxesTotal + feesTotal
  })

  const dishComps = computed(() => {
    if (!dishes.value) return cloneDeep(categories.value)

    const categoryShowArr = cloneDeep(categories.value)
    return categoryShowArr.map((el) => {
      const fetchedDishes = dishes.value.find((cat) => cat?.id === el.id)

      if (!fetchedDishes)
        return {
          ...el,
          subcategory: [
            ...el.subcategory,
            { id: 0, name: 'Other', dishes_count: el.other },
          ],
        }

      const hasSubCats = fetchedDishes.activedishes.some(
        (dish) =>
          dish.subcategories.length &&
          dish.subcategories.some((sc) => sc.parent_id === el.id)
      )

      if (!hasSubCats) {
        return {
          ...el,
          dishes: [...fetchedDishes.activedishes],
        }
      }

      const subcategory = el.subcategory
        .map((sub) => ({
          ...sub,
          dishes: fetchedDishes.activedishes.filter((subdish) =>
            subdish.subcategories.some((sc) => sc.id === sub.id)
          ),
        }))
        .filter((sub) => sub.dishes_count)

      if (el.other) {
        subcategory.push({
          id: 0,
          name: 'Other',
          dishes_count: el.other,
          dishes: fetchedDishes.activedishes.filter(
            (dish) =>
              !dish.subcategories.length ||
              !dish.subcategories.some((sc) => sc.parent_id === el.id)
          ),
        })
      }

      return {
        ...el,
        subcategory,
      }
    })
  })

  async function fetchStartingDishes(id: number = -1) {
    if (dishesLanguage.value !== $i18n.locale.value) {
      clearFetchedIds()
      dishesLanguage.value = $i18n.locale.value as string
    }
    isLoadingDishes.value = true
    const menu_id = activeMenuId.value
    const findIndex = categories.value.findIndex((cat) => cat.id === id)
    let ids: number[] = []
    if (id < 0) {
      ids = JSON.parse(useSafeStorage('cat_ids') || '[]')
    } else {
      ids = categories.value
        .filter((cat, index) => Math.abs(index - findIndex) <= 2)
        .map((c) => c.id)
    }
    const toStoreIds = [...ids]
    if (!ids.length) {
      isLoadingDishes.value = false
      return
    }

    useSetSafeStorage('cat_ids', JSON.stringify(toStoreIds))
    if (!ids.filter((id) => !fetched_ids.value.includes(id)).length) {
      setTimeout(() => {
        isLoadingDishes.value = false
      }, 300)
      return
    }
    try {
      await $axios
        .get('/restaurants/dishes-by-categories', {
          headers: { 'X-Request-Priority': 'high' },
          params: { categories: ids, menu_id, language: $i18n.locale.value },
        })
        .then((res) => {
          dishes.value = [...dishes.value, ...res.data.data]
          cat_ids.value = ids
          const newIds = ids.filter((id) => !fetched_ids.value.includes(id))
          if (newIds.length) {
            fetched_ids.value = [...fetched_ids.value, ...newIds]
          }
        })
    } catch (error) {
      console.error(error)
    } finally {
      isLoadingDishes.value = false
    }
  }

  function showEditDish(dish: SelectedDish) {
    singleDish.value = dish
  }

  async function fetchDishesByScroll(cat_id: number[]) {
    const fetchingCats = cat_id.filter((id) => !fetched_ids.value.includes(id))
    if (!fetchingCats.length || isLoadingDishes.value) return

    const menu_id = activeMenuId.value
    try {
      await $axios
        .get('/restaurants/dishes-by-categories', {
          headers: { 'X-Request-Priority': 'high' },
          params: {
            categories: fetchingCats,
            menu_id,
            language: $i18n.locale.value,
          },
        })
        .then((res) => {
          dishes.value = [...dishes.value, ...res.data.data]
          fetchingCats.forEach((cat_id) => {
            if (!fetched_ids.value.includes(cat_id)) {
              fetched_ids.value = [...fetched_ids.value, cat_id]
            }
          })
        })
    } catch (error) {
      console.error(error)
    }
  }

  async function fetchProductTypes() {
    try {
      const response = await $axios.get(`/restaurant/product-types`)
      product_types.value = response.data;
    } catch (error) {
      console.error('Failed to fetch product types:', error)
      product_types.value = []
    }
  }

  async function fetchDishDetails(dishId: number, categoryId: number) {
    try {
      const response = await $axios
        .get(`/restaurants/dishes/by-id`, {
          headers: { 'X-Request-Priority': 'high' },
          params: {
            id: dishId,
            category_id: categoryId,
            lang: $i18n.locale.value,
          },
        })
        .then((res) => {
          singleDish.value = res.data.data
        })
    } catch (error) {
      console.error('Failed to fetch dish details:', error)
    }
  }

  async function fetchDishDetailsForCard(dishId: number, categoryId: number) {
    const cacheKey = `${dishId}-${categoryId}-${$i18n.locale.value}`

    if (dishDetailsCache.value[cacheKey]) {
      return dishDetailsCache.value[cacheKey]
    }

    try {
      const response = await $axios.get(`/restaurants/dishes/by-id`, {
        headers: { 'X-Request-Priority': 'low' },
        params: {
          id: dishId,
          category_id: categoryId,
          lang: $i18n.locale.value,
        },
      })
      dishDetailsCache.value[cacheKey] = response.data.data
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch card dish details:', error)
      return null
    }
  }

  function areAddOnsEqual(a: selectedAddOn[], b: selectedAddOn[]): boolean {
    if (a.length !== b.length) return false

    const sortFn = (x: selectedAddOn) => `${x.id}-${x.count}-${x.amount}`
    const aSorted = [...a].sort((x, y) => sortFn(x).localeCompare(sortFn(y)))
    const bSorted = [...b].sort((x, y) => sortFn(x).localeCompare(sortFn(y)))
    return aSorted.every((addOn, idx) => {
      const other = bSorted[idx]
      return addOn.id === other.id && addOn.count === other.count
    })
  }

  function addDish(
    dish: Dish,
    count: number,
    options: null | {},
    addOns: selectedAddOn[],
    combo: null | {},
    instructions: null | string
  ) {
    const existingDish = selectedDishes.value.find(
      (d) =>
        d.id === dish.id &&
        d?.selectedCombination?.id === combo?.id &&
        areAddOnsEqual(addOns, d?.selectedAddOns) &&
        d?.instructions === instructions
    )
    if (existingDish) {
      if (existingDish.count + count === 0) {
        removeDish(dish.id, combo?.id || null, addOns, instructions)
        return
      }
      existingDish.count += count
      existingDish.selectedOptions = options
      existingDish.selectedCombination = combo
      existingDish.selectedAddOns = addOns
      existingDish.instructions = instructions
    } else {
      selectedDishes.value.push({
        ...dish,
        selectId: Date.now(),
        count: count,
        selectedOptions: options,
        selectedAddOns: addOns,
        selectedCombination: combo,
        instructions: instructions,
      })

    }

    syncLocalStorage()
  }

  function editDish(
    selectId: number,
    id: number,
    count: number,
    options: null | {},
    addOns: selectedAddOn[],
    combo: null | {},
    instructions: null | string
  ) {
    const existingDish = selectedDishes.value.find(
      (d) =>
        d.id === id &&
        d?.selectedCombination?.id === combo?.id &&
        areAddOnsEqual(addOns, d?.selectedAddOns) &&
        d?.instructions === instructions
    )

    const editingDish = selectedDishes.value.find(
      (d) => d.selectId === selectId
    )
    if (
      existingDish &&
      editingDish &&
      existingDish.selectId !== editingDish.selectId
    ) {
      existingDish.count += count
      setTimeout(() => {
        removeDish(
          editingDish?.id,
          editingDish?.selectedCombination?.id || null,
          editingDish?.selectedAddOns,
          editingDish?.instructions
        )
        syncLocalStorage()
      }, 80)

      return
    }

    if (editingDish) {
      if (count === 0) {
        setTimeout(() => {
          removeDish(
            editingDish?.id,
            editingDish?.selectedCombination?.id || null,
            editingDish?.selectedAddOns,
            editingDish?.instructions
          )
          syncLocalStorage()
        }, 80)
        return
      }

      editingDish.count = count
      editingDish.selectedOptions = options
      editingDish.selectedCombination = combo
      editingDish.selectedAddOns = addOns
      editingDish.instructions = instructions
    }

    syncLocalStorage()
  }

  function changeDishDescStatus(val: boolean) {
    isAnyOpenedDishDesc.value = val
  }

  function removeDish(
    id: number,
    comboId: number | null,
    addOns: selectedAddOn[],
    instruction: string | null
  ) {
    selectedDishes.value = selectedDishes.value.filter((dish) => {
      return !(
        dish.id === id &&
        (comboId ? dish?.selectedCombination?.id === comboId : true) &&
        areAddOnsEqual(addOns, dish.selectedAddOns) &&
        instruction === dish.instructions
      )
    })

    syncLocalStorage()
  }

  function addOrder(status: string, orderID?: number, short_code?: string) {
    const deliveryStore = useDeliveryStore()
    const deliveryData = deliveryStore.deliveryAddress;

    const hasDelivery =
      deliveryData.address?.trim() ||
      deliveryData.full_name?.trim() ||
      deliveryData.email?.trim() ||
      (deliveryData.phone && 'number' in deliveryData.phone && deliveryData.phone.number?.trim())

    const newObj: Order = {
      name: short_code || '',
      orderID: orderID ?? Math.floor(Math.random() * 89999) + 10000,
      order: selectedDishes.value,
      status: status,
      total: totalPrice.value + Number(tips.value ?? useSafeStorage('tips') ?? 0),
      tips: tips.value ?? Number(useSafeStorage('tips')),
      discountCode: discountCode.value,
      ...(hasDelivery && {
        delivery: {
          full_name: deliveryData.full_name ?? '',
          email: deliveryData.email ?? '',
          phone: deliveryData.phone && 'number' in deliveryData.phone ? deliveryData.phone : {},
          address: deliveryData.address ?? '',
          lon: deliveryData.lon ?? null,
          lat: deliveryData.lat ?? null,
        },
      }),
    }
    lastOrder.value = { ...newObj }
    ordersIds.value[slug.value] = ordersIds.value[slug.value] || []

    if (
      orderID &&
      ordersIds.value[slug.value] &&
      !(ordersIds.value[slug.value].map((obj) => Number(Object.keys(obj)[0]))).includes(orderID)
    ) {
      ordersIds.value[slug.value] = [
        { [orderID]: status || '' },
        ...ordersIds.value[slug.value],
      ]
      // .splice(0, 20)
      if (hasDelivery) {
        deliveryOrdersIds.value[slug.value] = deliveryOrdersIds.value[slug.value] || []
        deliveryOrdersIds.value[slug.value] = [
          { [orderID]: status || '' },
          ...deliveryOrdersIds.value[slug.value],
        ]
      }

      let allOrders = ordersIds.value[slug.value];

      if (allOrders.length > 20) {
        const dones = allOrders.filter(el => Object.values(el)[0] === 'done').reverse()
        const removeCount = allOrders.length - 20;
        const doneIdsToRemove = dones.slice(0, removeCount).map(el => Object.keys(el)[0]);

        allOrders = allOrders.filter(el => !doneIdsToRemove.includes(Object.keys(el)[0]));
      }
      ordersIds.value[slug.value] = allOrders
    }

    selectedDishes.value = []
    discountCode.value = ''
    tips.value = null

    syncLocalStorage()
  }

  const getOrders = async () => {
    if (user.value && user.value?.id) {
      await getUserOrders();
      return;
    }

    if (
      !ordersIds.value[slug.value] ||
      !ordersIds.value[slug.value].length
    )
      return

    try {
      const ids = ordersIds.value[slug.value].map((obj) => Number(Object.keys(obj)[0]))
      const response = await $axios.get(`/restaurant/get-orders`, {
        params: { table_id: getActiveServiceMethod() === 'dine_in' ? tableId.value : null, ids: ids },
      })
      if (response.data) {
        orders.value = response.data.data;
      }

      return response.data.data;
    } catch (error) {
      console.error(error)
    }
  }

  const getUserOrders = async () => {
    try {
      const response = await $axios.get(`/restaurant/users/${user.value?.id}/orders`);
      if (response.data) {
        orders.value = response.data.data || response.data
        lastOrder.value = orders.value[0]
        useSetSafeStorage('lastOrder', JSON.stringify(lastOrder.value ?? null))
      }

      return response.data.data || response.data
    } catch (error) {
      console.error(error)
    }
  }

  const getOrderById = async (orderID: number) => {
    try {
      const response = await $axios.get(`/restaurant/order-by-id`, {
        params: {
          lang: $i18n.locale.value,
          id: orderID,
        },
      })
      if (response.data) {
        showingSingleOrder.value = response.data.data;
        return response.data.data;
      }
    } catch (error: any) {
      console.error(error)
      if (error.response?.status === 404) {
        const canceledOrder = getCanceledOrderSnapshot(orderID)
        if (canceledOrder) {
          showingSingleOrder.value = {
            ...canceledOrder,
            status: { ...(canceledOrder.status ?? {}), slug: 'cancelled' },
            dish_order_linking: canceledOrder.dish_order_linking ?? canceledOrder.order_dishes ?? [],
          } as any
          return showingSingleOrder.value
        }

        showingSingleOrder.value = {
          id: orderID,
          short_code: 'OS-' + orderID,
          status: { slug: 'cancelled' },
          created_at: new Date().toISOString(),
          created_at_date_time: new Date().toISOString(),
          orderPriceComponents: [],
          dish_order_linking: [],
        } as any
        if (lastOrder.value && (lastOrder.value.id == orderID || lastOrder.value.orderID == orderID)) {
          lastOrder.value.status = 'cancelled'
          useSetSafeStorage('lastOrder', JSON.stringify(lastOrder.value))
        }
        return showingSingleOrder.value;
      }
    }
  }

  const getLastOrder = async () => {
    if (
      !tableId.value ||
      !ordersIds.value[slug.value] ||
      !ordersIds.value[slug.value].length
    )
      return

    try {
      const response = await $axios.get(`/restaurant/order-by-id`, {
        params: {
          lang: $i18n.locale.value,
          id: Object.keys(ordersIds.value[slug.value][0])[0],
        },
      })
      if (response.data) {
        const orderData = response.data.data || response.data[0] || response.data
        lastOrder.value = orderData
        useSetSafeStorage('lastOrder', JSON.stringify(lastOrder.value ?? null))
        return orderData
      }
    } catch (error: any) {
      console.error(error)
      if (error.response?.status === 404) {
        const orderId = Number(Object.keys(ordersIds.value[slug.value][0])[0])
        const canceledOrder = getCanceledOrderSnapshot(orderId)
        if (canceledOrder) {
          lastOrder.value = {
            ...canceledOrder,
            status: { ...(canceledOrder.status ?? {}), slug: 'cancelled' },
            dish_order_linking: canceledOrder.dish_order_linking ?? canceledOrder.order_dishes ?? [],
          } as any
          useSetSafeStorage('lastOrder', JSON.stringify(lastOrder.value ?? null))
          return lastOrder.value
        }

        lastOrder.value = {
          id: orderId,
          short_code: 'OS-' + orderId,
          status: { slug: 'cancelled' },
          created_at: new Date().toISOString(),
          created_at_date_time: new Date().toISOString(),
          orderPriceComponents: [],
          dish_order_linking: []
        } as any
        useSetSafeStorage('lastOrder', JSON.stringify(lastOrder.value ?? null))
        return lastOrder.value
      }
    }
  }

  async function getDishData(dish_id: number) {
    try {
      const response = await $axios.get(`/restaurant/dishes/${dish_id}`)
      return response.data
    } catch (error) {
      console.error(error)
    }
  }

  async function getAppliedRules(dishes: SelectedDish[] | null = null) {
    const targetDishes = dishes || selectedDishes.value
    const mappedDishes = targetDishes.map((dish: any) => {
      const price = parseFloat(
        dish.selectedCombination?.price ||
        (typeof dish.computed_price === 'string' ? Number(dish.computed_price) : dish.price) ||
        0
      )
      const mappedAddOns = (dish.selectedAddOns || []).map((addon: any) => ({
        id: addon.id,
        amount: addon.amount,
        count: addon.count,
        price_adjustment: addon.price_adjustment
      }))

      return {
        ...dish,
        price: price,
        computed_price: price,
        dish_add_ons: mappedAddOns,
        item: {
          id: dish.id,
          price: price,
          computed_price: price,
          dish_add_ons: mappedAddOns
        }
      }
    })

    const resp = await $axios.post('/restaurant/applied-rules', {
      restaurantSlug: slug.value,
      menu_id: menuIds.value,
      type: getActiveServiceMethod() === 'delivery' ? 'delivery' : 'dine_in',
      dishes: mappedDishes
    })

    applied_taxes.value = resp.data.taxes;
    applied_fees.value = resp.data.fees;
  }

  function clearOrders() {
    if (ordersIds.value?.[slug.value] && ordersIds.value[slug.value].length) {
      ordersIds.value[slug.value] = []
    }

    if (lastOrder.value) {
      lastOrder.value = null
      useRemoveSafeStorage('lastOrder');
    }

    showAlert('Tables.clearOrders', { table_name: name })

    syncLocalStorage()
  }

  function clearDishes() {
    selectedDishes.value = []

    syncLocalStorage()
  }

  function getDataFromStorage() {
    if (process.client) {
      const storedData = useSafeStorage('selectedDishes')
      const storedOrderData = useSafeStorage('lastOrder')
      const storedDeliveryOrderData = useSafeStorage('deliveryOrdersIds')
      const storedOrderIdsData = useSafeStorage('ordersIds')
      const storedTips = useSafeStorage('tips')

      if (storedData) {
        try {
          selectedDishes.value = JSON.parse(storedData)
        } catch (e) { }
      }

      if (storedOrderIdsData) {
        try {
          ordersIds.value = JSON.parse(storedOrderIdsData)
        } catch (e) { }
      }

      if (storedOrderData) {
        try {
          lastOrder.value = JSON.parse(storedOrderData)
        } catch (e) { }
      }

      if (storedDeliveryOrderData) {
        try {
          deliveryOrdersIds.value = JSON.parse(storedDeliveryOrderData)
        } catch (e) { }
      }

      if (storedTips) {
        tips.value = Number(storedTips) || null
      }
    }
  }

  onMounted(() => {
    getDataFromStorage()
  })

  function syncLocalStorage() {
    useSetSafeStorage(
      'selectedDishes',
      JSON.stringify(selectedDishes.value ?? [])
    )
    useSetSafeStorage('lastOrder', JSON.stringify(lastOrder.value ?? null))
    useSetSafeStorage('ordersIds', JSON.stringify(ordersIds.value ?? {}))
    useSetSafeStorage('deliveryOrdersIds', JSON.stringify(deliveryOrdersIds.value ?? {}))
  }

  function clearFetchedIds() {
    fetched_ids.value = []
    dishes.value = []
  }

  return {
    dishes,
    symbol,
    iso_code,
    selectedDishes,
    totalPrice,
    isLoadingDishes,
    lastOrder,
    discountCode,
    tips,
    instractions,
    isAnyOpenedDishDesc,
    cat_ids,
    fetched_ids,
    singleDish,
    dishComps,
    ordersIds,
    orders,
    deliveryOrdersIds,
    showingSingleOrder,
    searchQuery,
    applied_taxes,
    applied_fees,
    transactionVersion,
    totalRulesPrice,
    product_types,
    showLoginRequired,

    fetchStartingDishes,
    fetchDishesByScroll,
    addDish,
    removeDish,
    getDishData,
    addOrder,
    editDish,
    fetchDishDetails,
    fetchDishDetailsForCard,
    changeDishDescStatus,
    showEditDish,
    getOrders,
    getOrderById,
    getLastOrder,
    getAppliedRules,
    fetchProductTypes,
    clearOrders,
    clearDishes,
    clearFetchedIds,
    getDataFromStorage,
  }
})
